// Pure math + queries for a quadrilateral playmat grid. A grid is
// defined by four world-space corner points and optional column/row
// divider arrays (values in [0..1] along the top/left edges); when the
// divider arrays are absent the cells are uniformly spaced.
//
// Allowing the corners to move independently means the grid can be
// drawn skewed across the playmat rather than only as an axis-aligned
// rectangle. Useful when the camera or the mat itself isn't square-on.
//
// No Three.js and no state here. The renderer and the editor build on
// top of these functions.

export const DEFAULT_GRID = {
  topLeft: { x: -60, z: -50 },
  topRight: { x: 60, z: -50 },
  bottomLeft: { x: -60, z: 50 },
  bottomRight: { x: 60, z: 50 },
  cols: 5,
  rows: 4,
  colDividers: [0.2, 0.4, 0.6, 0.8],
  rowDividers: [0.25, 0.5, 0.75],
};

export function computeGridPositions(grid) {
  const cDivs = grid.colDividers || Array.from({ length: grid.cols - 1 }, (_, i) => (i + 1) / grid.cols);
  const rDivs = grid.rowDividers || Array.from({ length: grid.rows - 1 }, (_, i) => (i + 1) / grid.rows);
  return {
    colPositions: [0, ...cDivs, 1],
    rowPositions: [0, ...rDivs, 1],
  };
}

// Bilinear interpolation across the four corners. (colT, rowT) in [0..1].
export function getGridPoint(grid, colT, rowT) {
  const { topLeft, topRight, bottomLeft, bottomRight } = grid;
  const topX = topLeft.x + (topRight.x - topLeft.x) * colT;
  const topZ = topLeft.z + (topRight.z - topLeft.z) * colT;
  const botX = bottomLeft.x + (bottomRight.x - bottomLeft.x) * colT;
  const botZ = bottomLeft.z + (bottomRight.z - bottomLeft.z) * colT;
  return {
    x: topX + (botX - topX) * rowT,
    z: topZ + (botZ - topZ) * rowT,
  };
}

// Resolve a world-space {x,z} to a cell, or null if outside the grid.
// The returned cell carries center, dimensions, and corner points so
// callers can snap or highlight without recomputing them.
export function getCellAt(grid, x, z) {
  if (!grid) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);

  let col = -1;
  let row = -1;
  for (let c = 0; c < colPositions.length - 1; c++) {
    const left = getGridPoint(grid, colPositions[c], 0.5);
    const right = getGridPoint(grid, colPositions[c + 1], 0.5);
    if (x >= Math.min(left.x, right.x) && x <= Math.max(left.x, right.x)) { col = c; break; }
  }
  for (let r = 0; r < rowPositions.length - 1; r++) {
    const top = getGridPoint(grid, 0.5, rowPositions[r]);
    const bot = getGridPoint(grid, 0.5, rowPositions[r + 1]);
    if (z >= Math.min(top.z, bot.z) && z <= Math.max(top.z, bot.z)) { row = r; break; }
  }
  if (col < 0 || row < 0) return null;

  const tl = getGridPoint(grid, colPositions[col], rowPositions[row]);
  const tr = getGridPoint(grid, colPositions[col + 1], rowPositions[row]);
  const bl = getGridPoint(grid, colPositions[col], rowPositions[row + 1]);
  const br = getGridPoint(grid, colPositions[col + 1], rowPositions[row + 1]);

  return {
    col,
    row,
    centerX: (tl.x + tr.x + bl.x + br.x) / 4,
    centerZ: (tl.z + tr.z + bl.z + br.z) / 4,
    width: Math.abs(tr.x - tl.x),
    height: Math.abs(bl.z - tl.z),
    tl,
    tr,
    bl,
    br,
    totalCols: colPositions.length - 1,
    totalRows: rowPositions.length - 1,
  };
}

export function getNearestIntersection(grid, x, z) {
  if (!grid) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);

  let bestDist = Infinity;
  let best = null;

  for (let c = 0; c < colPositions.length; c++) {
    for (let r = 0; r < rowPositions.length; r++) {
      const pt = getGridPoint(grid, colPositions[c], rowPositions[r]);
      const dx = pt.x - x;
      const dz = pt.z - z;
      const dist = dx * dx + dz * dz;
      if (dist < bestDist) {
        bestDist = dist;
        const adjacent = [];
        if (c > 0 && r > 0) adjacent.push({ col: c - 1, row: r - 1 });
        if (c < colPositions.length - 1 && r > 0) adjacent.push({ col: c, row: r - 1 });
        if (c > 0 && r < rowPositions.length - 1) adjacent.push({ col: c - 1, row: r });
        if (c < colPositions.length - 1 && r < rowPositions.length - 1) adjacent.push({ col: c, row: r });
        best = { col: c, row: r, x: pt.x, z: pt.z, adjacentCells: adjacent };
      }
    }
  }
  return best;
}

// Build a fresh axis-aligned grid spanning the rectangle (start, end)
// with uniform cols × rows cells. Used to seed the grid from a single
// drag before the user fine-tunes intersection handles.
export function gridFromDragRect(start, end, { cols = 5, rows = 4 } = {}) {
  const minX = Math.min(start.x, end.x);
  const maxX = Math.max(start.x, end.x);
  const minZ = Math.min(start.z, end.z);
  const maxZ = Math.max(start.z, end.z);
  return {
    topLeft: { x: minX, z: minZ },
    topRight: { x: maxX, z: minZ },
    bottomLeft: { x: minX, z: maxZ },
    bottomRight: { x: maxX, z: maxZ },
    cols,
    rows,
    colDividers: Array.from({ length: cols - 1 }, (_, i) => (i + 1) / cols),
    rowDividers: Array.from({ length: rows - 1 }, (_, i) => (i + 1) / rows),
  };
}

// Resolve a Cell (from getCellAt) to its playmat number. Numbering is
// anchored to player 1's seat: cell 1 sits at the bottom-right corner
// of P1's view, increments right-to-left across the bottom row, then
// climbs row by row up to cell totalRows*totalCols at the top-left.
// For the canonical 5×4 playmat: cell 1 = (col=4, row=3), cell 5 =
// (col=0, row=3), cell 16 = (col=4, row=0), cell 20 = (col=0, row=0).
// Both players see the same number for the same physical cell, so the
// log stays identical on either side of the table.
export function getCellNumber(cell) {
  if (!cell) return null;
  const { col, row, totalCols, totalRows } = cell;
  return (totalRows - 1 - row) * totalCols + (totalCols - col);
}

// Apply a drag of an intersection handle to a new world-space point.
// Corner handles move the corresponding grid corner; interior/edge
// handles instead slide their column or row divider. Interior handles
// move both (they sit on a col×row crossing).
export function applyHandleDrag(grid, handleMeta, point) {
  const { colIndex, rowIndex, isCorner, colPositions, rowPositions } = handleMeta;
  const next = { ...grid };

  if (isCorner) {
    const isTop = rowIndex === 0;
    const isLeft = colIndex === 0;
    if (isTop && isLeft) next.topLeft = { x: point.x, z: point.z };
    else if (isTop && !isLeft) next.topRight = { x: point.x, z: point.z };
    else if (!isTop && isLeft) next.bottomLeft = { x: point.x, z: point.z };
    else next.bottomRight = { x: point.x, z: point.z };
    return next;
  }

  const { topLeft: tl, topRight: tr, bottomLeft: bl } = next;

  if (colIndex > 0 && colIndex < colPositions.length - 1) {
    const topSpanX = tr.x - tl.x;
    const topSpanZ = tr.z - tl.z;
    let colT;
    if (Math.abs(topSpanX) > Math.abs(topSpanZ)) {
      colT = topSpanX !== 0 ? (point.x - tl.x) / topSpanX : 0.5;
    } else {
      colT = topSpanZ !== 0 ? (point.z - tl.z) / topSpanZ : 0.5;
    }
    colT = Math.max(0.02, Math.min(0.98, colT));
    const dividers = [...(next.colDividers || Array.from({ length: next.cols - 1 }, (_, i) => (i + 1) / next.cols))];
    dividers[colIndex - 1] = Math.round(colT * 1000) / 1000;
    dividers.sort((a, b) => a - b);
    next.colDividers = dividers;
  }

  if (rowIndex > 0 && rowIndex < rowPositions.length - 1) {
    const leftSpanX = bl.x - tl.x;
    const leftSpanZ = bl.z - tl.z;
    let rowT;
    if (Math.abs(leftSpanZ) > Math.abs(leftSpanX)) {
      rowT = leftSpanZ !== 0 ? (point.z - tl.z) / leftSpanZ : 0.5;
    } else {
      rowT = leftSpanX !== 0 ? (point.x - tl.x) / leftSpanX : 0.5;
    }
    rowT = Math.max(0.02, Math.min(0.98, rowT));
    const dividers = [...(next.rowDividers || Array.from({ length: next.rows - 1 }, (_, i) => (i + 1) / next.rows))];
    dividers[rowIndex - 1] = Math.round(rowT * 1000) / 1000;
    dividers.sort((a, b) => a - b);
    next.rowDividers = dividers;
  }

  return next;
}
